import { useEffect, useRef, useState } from 'react';

function useCountUp(target: number, active: boolean, duration = 1600) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf: number;
    const start = performance.now();
    const step = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return value;
}

export default function Savings() {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(true)),
      { threshold: 0.35 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const main = useCountUp(1840, active);
  const years = useCountUp(7.2, active);
  const offset = useCountUp(89, active);
  const lifetime = useCountUp(46, active);

  return (
    <section id="savings" ref={ref} className="py-[72px] md:py-[144px] bg-solar-bg">
      <div className="container-x">
        <div className="grid grid-cols-12 gap-6 mb-10 md:mb-24">
          <div className="col-span-12 md:col-span-8">
            <p className="text-xs font-medium text-solar-green tracking-wider uppercase">Savings & ROI</p>
            <h2 className="display-font mt-4 text-[34px] md:text-[56px] font-semibold leading-[1.02] text-solar-ink">
              The math is quiet.<br/>The savings are not.
            </h2>
          </div>
        </div>

        {/* Anchor number */}
        <div className="grid grid-cols-12 gap-6 items-end mb-14 md:mb-24">
          <div className="col-span-12 md:col-span-7">
            <div className="display-font text-[72px] sm:text-[140px] md:text-[200px] leading-[0.9] font-semibold text-solar-ink tracking-tight">
              ${Math.round(main).toLocaleString()}
            </div>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 pb-4">
            <p className="text-solar-muted text-sm uppercase tracking-wider font-medium mb-2">Estimated annual savings</p>
            <p className="text-solar-muted text-[15px] leading-relaxed">
              Average residential customer, 6.4 kW system, U.S. baseline utility rates. Actual results depend on roof, region and consumption.
            </p>
          </div>
        </div>

        {/* Supporting stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-8 pt-8 md:pt-10 border-t border-solar-divider">
          {[
            { value: <><span>{years.toFixed(1)}</span><span className="text-solar-muted text-[22px] md:text-[26px] font-normal ml-1.5">yr</span></>, copy: 'Estimated payback period' },
            { value: <><span>{Math.round(offset)}</span><span className="text-solar-green">%</span></>, copy: 'Electricity offset from solar' },
            { value: <>${Math.round(lifetime)}k</>, copy: 'Projected 25-year lifetime savings' },
          ].map((m, i) => (
            <div key={i} className="flex flex-col items-start text-left w-full">
              <div className="display-font text-[40px] md:text-[64px] font-semibold text-solar-ink leading-none tabular-nums">
                {m.value}
              </div>
              <p className="mt-5 text-solar-muted text-[14px] leading-relaxed">{m.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}